import { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { User } from "../types";
import { ApiError, apiServerUrl, messageForThrownError, parseErrorMessage } from "../services/ApiService";

const SESSION_TOKEN_STORAGE_KEY =
  import.meta.env.VITE_SESSION_TOKEN_STORAGE_KEY ?? "easyflexSessionToken";
const USER_STORAGE_KEY =
  import.meta.env.VITE_USER_STORAGE_KEY ?? "easyflexUser";

interface UserContextType {
  user: User | null;
  sessionToken: string | null;
  loading: boolean;
  error: string | null;
  setUserSession: (user: User, sessionToken: string) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // Iframe auth: parent app passes an Easyflex JWT via ?token= param.
    // Exchange it for an internal session token via the backend.
    const easyflexJwt = params.get("token");
    if (easyflexJwt) {
      // Clear stale session before exchanging
      window.localStorage.removeItem(USER_STORAGE_KEY);
      window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);

      fetch(`${apiServerUrl}/easyflex/auth/exchange`, {
        method: "POST",
        headers: { Authorization: `Bearer ${easyflexJwt}` },
      })
        .then(async (res) => {
          if (!res.ok) {
            throw new ApiError(await parseErrorMessage(res));
          }
          return res.json();
        })
        .then((data: { user: User; session_token: string }) => {
          const url = new URL(window.location.href);
          url.searchParams.delete("token");
          window.history.replaceState({}, "", url.toString());

          setUser(data.user);
          setSessionToken(data.session_token);
          window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
          window.localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, data.session_token);
        })
        .catch((err) => {
          console.error("Failed to exchange Easyflex token", err);
          // Only an ApiError carries a Dutch message; a raw fetch failure does not.
          setError(err instanceof ApiError ? err.message : messageForThrownError(err));
        })
        .finally(() => setLoading(false));
      return;
    }

    try {
      const storedToken = window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
      const storedUser = window.localStorage.getItem(USER_STORAGE_KEY);

      if (storedToken && storedUser) {
        const parsedUser: User = JSON.parse(storedUser);
        setUser(parsedUser);
        setSessionToken(storedToken);
      }
    } catch (err) {
      console.error("Failed to restore user session", err);
      setError("Kon sessie niet herstellen");
    } finally {
      setLoading(false);
    }
  }, []);

  const setUserSession = (nextUser: User, nextToken: string) => {
    setUser(nextUser);
    setSessionToken(nextToken);
    setError(null);
    setLoading(false);

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
        window.localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, nextToken);
      } catch (err) {
        console.error("Failed to persist user session", err);
        setError("Kon sessie niet opslaan");
      }
    }
  };

  const logout = () => {
    setUser(null);
    setSessionToken(null);
    setError(null);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(USER_STORAGE_KEY);
      window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    }
  };

  // Listen for auth errors from API calls and trigger logout
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleAuthError = () => {
      console.warn("Session expired or invalid, logging out");
      logout();
    };

    window.addEventListener("auth-error", handleAuthError);
    return () => window.removeEventListener("auth-error", handleAuthError);
  }, []);

  const value: UserContextType = {
    user,
    sessionToken,
    loading,
    error,
    setUserSession,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with the component or provider that uses it
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
